
import React, { useState } from 'react';
import { Button } from './ui/button';
import { createPaypalSubscription, getPaypalPlanIdForTier } from '../services/paypalService';
import { Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription } from '../hooks/useSubscription';

interface PayPalButtonProps {
  tier: string;
  billingCycle?: 'monthly' | 'annual';
  label?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  disabled?: boolean;
}

export function PayPalButton({ 
  tier, 
  billingCycle = 'monthly',
  label = "Subscribe with PayPal", 
  variant = "outline",
  size = "default",
  disabled = false
}: PayPalButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { subscription } = useSubscription();

  const handleSubscription = async () => {
    console.log("PayPal button clicked - starting subscription process");
    
    if (isLoading) {
      console.log("Already loading, ignoring click");
      return;
    }

    // Check if user already has an active subscription
    if (subscription?.data?.currentTier && subscription.data.currentTier !== 'free') {
      toast.error("You already have an active subscription", {
        description: "To change plans, please cancel your current subscription first through PayPal Customer Portal, then subscribe to a new plan.",
        duration: 8000
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log(`Creating PayPal subscription for tier: ${tier}, billing: ${billingCycle}`);
      
      const planId = getPaypalPlanIdForTier(tier, billingCycle);
      console.log(`Using plan ID: ${planId}`);
      
      toast.info("Preparing PayPal checkout...");
      
      console.log("Calling createPaypalSubscription...");
      const result = await createPaypalSubscription(planId);
      console.log("PayPal subscription result:", result);
      
      if (result.success && result.approve_url) {
        console.log("Redirecting to PayPal:", result.approve_url);
        
        // Show success message before redirect
        toast.success("Redirecting to PayPal...");
        
        // Add a small delay to ensure the toast shows
        setTimeout(() => {
          // Redirect to PayPal in the same window
          window.location.href = result.approve_url;
        }, 500);
        
      } else {
        console.error("PayPal subscription creation failed:", result.error);
        
        // Check if it's a development environment issue
        if (result.error?.includes('development') || result.error?.includes('domain')) {
          toast.error("Development Environment Limitation", {
            description: (
              <div className="space-y-2">
                <p>{result.error}</p>
                <p className="text-sm">
                  <ExternalLink className="inline h-3 w-3 mr-1" />
                  Try using your live domain for PayPal subscriptions.
                </p>
              </div>
            ),
            duration: 10000
          });
        } else {
          toast.error(result.error || "Failed to initiate PayPal checkout");
        }
      }
    } catch (error) {
      console.error("Failed to start PayPal checkout:", error);
      toast.error("Something went wrong. Please try again on your live domain.");
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user has active subscription and this is a different tier
  const hasActiveSubscription = subscription?.data?.currentTier && subscription.data.currentTier !== 'free';
  const isDifferentTier = hasActiveSubscription && subscription.data.currentTier !== tier;
  const isCurrentTier = subscription?.data?.currentTier === tier;

  // Use gradient styling for all non-disabled buttons
  const buttonClassName = (disabled || isCurrentTier)
    ? "gap-2 w-full" 
    : "gap-2 w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0";

  return (
    <Button
      onClick={handleSubscription}
      disabled={isLoading || disabled || isCurrentTier}
      variant={(disabled || isCurrentTier) ? variant : "default"}
      size={size}
      className={buttonClassName}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Processing...
        </>
      ) : isDifferentTier ? (
        <>
          <AlertCircle className="h-4 w-4" />
          Cancel Current First
        </>
      ) : (
        <>
          <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20.067 8.478c.492.88.556 1.937.3 3.228-1.495 7.522-10.382 5.237-13.073 5.237l-1.138 4.528h-2.969l2.353-9.722c-1.301-.192-2.248-.4-2.248-.4l.462-1.828s2.85.498 4.337.498c1.488 0 1.825-.234 1.99-1.113l1.358-5.404c4.053 0 7.953.399 8.628 4.976zm-2.896.193c-.234-1.021-1.258-1.454-2.668-1.49h-.432l-1.328 5.83c2.324 0 4.599-1.346 4.428-4.34z" fill="currentColor"/>
          </svg>
          {label}
        </>
      )}
    </Button>
  );
}
